<script setup lang="ts">
import { roundTo, notifyError, emptyMongoId, to_wd_dd_MMMM } from '@/utils/helpers'
import { unitésParBoîte, getDateCoupéForCulture, culturesGérées } from '@/utils/helpers-vegco'
//import type { MDBVegcoBucket } from '@/types/vegco/bucket'
//import type { MDBVegcoCommandeVente } from '@/types/vegco/commandevente'
import type { ECOption } from '@/plugins/echarts'

definePage({
  meta: {
    id: 'vgco.bucketssemaine',
    title: 'Buckets / semaine',
    icon: 'mdi-bucket',
    drawerIndex: 2,
    requiresAuth: true,
    requiresSiteAccess: 'vegco'
  },
})

const theme = useTheme()
const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const isThisWeek = ref(false)
//const onlyThisWeek = ref(true)
const weekSelector = useTemplateRef('weekSelector')
const selectedWeek = ref(new Date(storeU.journéeSélectionnée))
const numberOfDays = ref(7)
const numberOfWeeks = ref(1)
const days = ref([] as Date[])
const columnDays = ref([] as Date[])
const differences = [] as number[]
const balise = ref('Toutes')
const balisesChoix = ref([] as string[])
const totalProduction = ref(0)
const totalCommandes = ref(0)
let legendsSelected: Record<string, boolean> = {}

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.openFew(culturesGérées())
  if (result != undefined && result != '') {
    storeU.sélectionnerCulture(result, null)
    await getData()
  }
}

watch(selectedWeek, async (_) => {
  await nextTick()
  getData()
})

watch (balise, async (_) => {
  await nextTick()
  updateChartNew()
})

async function onWeekNumberChanged() {
  numberOfDays.value = numberOfWeeks.value * 7
  await nextTick()
  getData()
}

async function getData() {
  try{
    if (storeU.cultureSélectionnée.nom == undefined) {
      notifyError('Veuillez sélectionner une culture avant de continuer.')
      return
    }

    if (weekSelector.value == undefined) {
      notifyError('Sélecteur de semaine non trouvé.')
      return
    }

    //const lastPreviousDays = onlyThisWeek.value ? 0 : 180
    const lastPreviousDays = 21

    isLoading.value = true
    const weekStart = weekSelector.value.getWeekStart()
    weekStart.setDate(weekStart.getDate() - lastPreviousDays)
    const weekEnd = weekSelector.value.getWeekEnd()
    isThisWeek.value = new Date() > weekStart && new Date() < weekEnd

    const date = new Date(weekStart)
    days.value = []
    columnDays.value = []
    for (let i = 0; i < lastPreviousDays + numberOfDays.value; i++) {
      if (i >= lastPreviousDays) {
        columnDays.value.push(new Date(date.getTime()))
      }

      days.value.push(new Date(date.getTime()))
      date.setDate(date.getDate() + 1)
    }

    await storeU.fetchVegcoProduitsIfNeeded()
    await storeU.fetchBalisesIfNeeded()
    await storeVegco.loadDays(weekStart, weekEnd)

    balise.value = 'Toutes'
    balisesChoix.value = []
    balisesChoix.value.push('Toutes')
    balisesChoix.value.push(...storeU.getBalises('Bucket', 'Culture', storeU.cultureSélectionnée.nom, null, null).map(b => b.nom))

    crunchData()
    updateChartNew()

    newData.value = false
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

function getXAxisNames() {
  const names = [] as string[]
  names.push(...columnDays.value.map(d => to_wd_dd_MMMM(d)))
  return names
}

const crunches = [] as any[]
function crunchData() {
  crunches.length = 0
  totalProduction.value = 0
  totalCommandes.value = 0
  const buckets = storeVegco.bucketsStore
  const culture = storeU.cultureSélectionnée.nom
  const unitésBoîte = unitésParBoîte(culture)
  const invBuckets = buckets.filter((b) => b.semiId === emptyMongoId() && b.culture === culture &&
      b.commandeVenteId == emptyMongoId())
  const dateTodayMin = new Date()
  dateTodayMin.setHours(0, 0, 0, 0)

  let carries = 0
  for (const day of days.value) {
    const entry = { date: day, production: 0, commandes: 0, inventory: 0, carry: carries, buckets: [] as any[] }

    const dateMin = new Date(day.getFullYear(), day.getMonth(), day.getDate())
    const dateMax = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999)

    // Buckets
    for (const site of storeU.lettresDappelSites) {
      if (site.lettresDappel == 'VGCO') {
        continue // Skip VGCO site
      }
      const entrySite = { siteId: site.id, siteLD: site.lettresDappel, total: 0 }
      const bucketsDay = buckets.filter((b) => {
        const d = new Date(b.date)
        return b.siteId == site.id && d >= dateMin && d <= dateMax && b.culture === culture
      })
      for (const bucket of bucketsDay) {
        if (balise.value != 'Toutes' && !bucket.balises.includes(balise.value)) {
          continue
        }

        const qté = roundTo(bucket.quantité / unitésBoîte, 0)
        entrySite.total += qté
        entry.production += qté
        if (bucket.commandeVenteId != emptyMongoId()) {
          entry.commandes += qté
        }
        if (day >= dateTodayMin && bucket.commandeVenteId == emptyMongoId()) {
          carries += qté
        }
      }
      entry.buckets.push(entrySite)
    }

    // Inventory
    for (const bucket of invBuckets) {
      const d = new Date(bucket.date)
      if (d < dateMin || d > dateMax) {
        continue // Skip inventory outside the selected day
      }
      if (balise.value != 'Toutes') {
        const produit = storeU.getVegcoProduit(bucket.codeProduit)
        if (!produit || balise.value != produit.balise) {
          continue
        }
      }

      const qté = roundTo(bucket.quantité / unitésBoîte, 0)
      entry.inventory += qté
      entry.production += qté
      if (day >= dateTodayMin) {
        carries += qté
      }
    }

    // Commandes
    for (const commande of storeVegco.commandesStore) {
      if (commande.statut == 'Annulé' || commande.statut == 'Estimé') {
        continue // Skip cancelled commandes
      }

      const date = getDateCoupéForCulture(commande, culture)
      if (date < dateMin || date > dateMax) {
        continue // Skip commandes outside the selected day
      }

      for (const détail of commande.détails) {
        if (détail.culture != culture) continue

        if (balise.value != 'Toutes') {
          const produit = storeU.getVegcoProduit(détail.codeProduit)
          if (!produit || balise.value != produit.balise) {
            continue
          }
        }

        let qté = détail.quantité
        for (const bucket of buckets) {
          if (bucket.culture == détail.culture && bucket.commandeVenteId == commande.id &&
              bucket.codeProduit == détail.codeProduit) {
            qté -= bucket.quantité
          }
        }

        qté = roundTo(qté / unitésBoîte, 0)
        entry.commandes += qté
        if (day >= dateTodayMin) {
          carries -= qté
        }
      }
    }

    // Carry
    totalProduction.value += entry.production
    totalCommandes.value += entry.commandes

    crunches.push(entry)
  }
}

const chart = useTemplateRef('chart')

function updateChartNew() {
  const axisNames = getXAxisNames();
  (option.value.xAxis as any)[0].data = axisNames
  option.value.series = []
  chart.value?.clear()
  if (!weekSelector.value) {
    return;
  }
  const weekStart = weekSelector.value.getWeekStart()
  const weekEnd = weekSelector.value.getWeekEnd()

  // Buckets
  for (const site of storeU.lettresDappelSites) {
    if (site.lettresDappel == 'VGCO') {
      continue // Skip VGCO site
    }
    const totalData = [] as number[]
    for (const crunch of crunches) {
      if (crunch.date < weekStart || crunch.date > weekEnd) {
        continue // Skip days outside the selected week
      }
      const bucket = crunch.buckets.find((b: { siteId: string }) => b.siteId === site.id)
      if (!bucket) {
        totalData.push(0)
        continue
      }
      totalData.push(bucket.total)
    }
    (option.value.series as any[]).push({
      name: storeVegco.getSiteLettresDappelRenommé(site.id), stack: 'Buckets', type: 'bar',
      color: storeVegco.getSiteCouleur(site.lettresDappel),
      emphasis: { focus: 'series' },
      data: totalData
    });
  }

  // Add inventory
  {
    const totalData = [] as number[]
    for (const crunch of crunches) {
      if (crunch.date < weekStart || crunch.date > weekEnd) {
        continue // Skip days outside the selected week
      }
      totalData.push(crunch.inventory)
    }
    (option.value.series as any[]).push({
      name: 'Inventaire', stack: 'Buckets', type: 'bar',
      color: storeVegco.getSiteCouleur('VGCO'), // Use VGCO color for inventory
      emphasis: { focus: 'series' },
      data: totalData
    })
  }

  // Add commandes
  {
    const totalData = [] as number[]
    for (const crunch of crunches) {
      if (crunch.date < weekStart || crunch.date > weekEnd) {
        continue // Skip days outside the selected week
      }
      totalData.push(crunch.commandes)
    }
    (option.value.series as any[]).push({
      name: 'Commandes', stack: 'Commandes', type: 'bar',
      color: '#FF40D0', // Pink
      emphasis: { focus: 'series' },
      data: totalData
    })
  }

  // Carries
  {
    differences.length = 0
    const totalDataAdd = [] as number[]
    const totalDataSub = [] as number[]
    for (const crunch of crunches) {
      if (crunch.date < weekStart || crunch.date > weekEnd) {
        continue // Skip days outside the selected week
      }

      if (crunch.carry >= 0) {
        totalDataAdd.push(crunch.carry)
        totalDataSub.push(0)
      } else {
        totalDataAdd.push(0)
        totalDataSub.push(-crunch.carry)
      }
      differences.push(crunch.production - crunch.commandes + crunch.carry)
    }

    (option.value.series as any[]).push({
      name: 'Surplus rapporté', stack: 'Buckets', type: 'bar',
      color: '#158015', // Green
      emphasis: { focus: 'series' },
      data: totalDataAdd
    });

    (option.value.series as any[]).push({
      name: 'Manque rapporté', stack: 'Commandes', type: 'bar',
      color: '#A01010', // Red
      emphasis: { focus: 'series' },
      data: totalDataSub
    });
  }

  addGrandTotals()
}

function addGrandTotals() {
  if (option.value.series) {
    (option.value.series as any[]).map((s) => (legendsSelected[s.name] = true));
  }

  const zeroes = []
  for (let i = 0; i < numberOfDays.value; i++) {
    zeroes.push(0)
  }

  (option.value.series as any[]).push({
    name: '', stack: 'Buckets', type: 'bar',
    data: zeroes,
    tooltip: { show: false },
    label: {
      show: true,
      position: 'top',
      formatter: (p: { name: any }) => {
        let sum = 0;
        if (option.value.series && option.value.xAxis) {
          const icat = (option.value.xAxis as any[])[0].data.findIndex((e: any) => e == p.name);
          (option.value.series as any[]).forEach((s) => {
            if (s.stack == 'Buckets' && legendsSelected[s.name]) {
              sum += s.data[icat] || 0;
            }
          });
        }
        return sum;
      }
    },
  });

  (option.value.series as any[]).push({
    name: '', stack: 'Commandes', type: 'bar',
    data: zeroes,
    tooltip: { show: false },
    label: {
      show: true,
      position: 'top',
      formatter: (p: { name: any }) => {
        let sum = 0;
        if (option.value.xAxis) {
          const icat = (option.value.xAxis as any[])[0].data.findIndex((e: any) => e == p.name);
          (option.value.series as any[]).forEach((s) => {
            if (s.stack == 'Commandes' && legendsSelected[s.name]) {
              sum -= s.data[icat] || 0;
            }
          });
        }
        return sum;
      }
    },
  });
}

const option = ref<ECOption>({
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
  legend: {},
  grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
  xAxis: [
    {
      type: 'category',
      data: getXAxisNames(),
      axisLabel: {
        formatter: function (value: number, index: number) {
          const diff = roundTo(differences[index], 2)
          const label = (diff >= 0 ? `+${diff}` : `${diff}`)
          return `Prod. | Cmds.\n\n${value}\n (${label})`;
        },
        textStyle: { fontSize: 14 }
      } as any
    }
  ],
  yAxis: [
    {
      type: 'value',
      min: 0,
      max: function (value : any) { return Math.max(roundTo((value.max + 1000) / 1000, 0) * 1000, 2000) }
    }
  ],
  series: []
});

function onLegendSelectChanged(event: any) {
  legendsSelected = event.selected
  chart.value?.setOption({})
}

const intervalId = setInterval(async () => {
  if (storeU.cultureSélectionnée.nom == undefined) return
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 3000)

onMounted(() => {
  getData()
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" style="height: 85vh; display: flex; flex-direction: column;" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title + culture chip + selector -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-bucket" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Buckets / semaine</div>
            <div class="text-caption text-medium-emphasis">Production par semaine</div>
          </div>
          <v-chip
            color="primary" variant="tonal" size="large" class="ms-1 culture-chip"
            append-icon="mdi-chevron-down" style="cursor: pointer; font-weight: 600;"
            @click="showDialogCulture">
            {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
          </v-chip>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn v-if="newData" color="primary" variant="text" prepend-icon="mdi-refresh" @click="getData()">
            Recharger les données
          </v-btn>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <WeekSelector ref="weekSelector" v-model="selectedWeek" v-model:number-of-days="numberOfDays"/>
        <v-spacer/>
        <v-text-field
          v-model="numberOfWeeks" label="Nombre de semaines"
          type="number" min="1" max="3"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 200px;"
          @change="onWeekNumberChanged"/>
        <v-select
          v-model="balise"
          :items="balisesChoix" label="Balises"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 400px;"/>
      </div>
    </div>
    <v-divider/>
    <v-chart
      ref='chart' :option="option"
      style="flex: 1 1 auto; min-width: 200px; min-height: 100px;" autoresize
      @legendselectchanged="onLegendSelectChanged"/>
  </v-card>

  <DialogCulture ref='dialogCulture' />
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Culture chip hover ───────────────────── */
.culture-chip {
  transition: opacity 0.15s;
}
.culture-chip:hover {
  opacity: 0.78;
}
</style>
